/**
 * LineageTimeTravelPanel
 *
 * Component for time-traveling through lineage history snapshots.
 * Allows users to view the lineage graph as it existed at a specific point in time.
 */

import React, { useState, useMemo } from 'react';
import { History, Clock, ChevronDown, ChevronUp, Play } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import {
  useSnapshots,
  useSnapshotStats,
  useTimeTravelMutation,
} from '@/features/lineage/hooks/useLineage';
import type {
  LineageSnapshot,
  LineageGraph,
  SnapshotType,
} from '@/features/lineage/types/lineage.types';

interface LineageTimeTravelPanelProps {
  onTimeTravelComplete?: (graph: LineageGraph, timestamp: string) => void;
  entityId?: string;
  className?: string;
}

export const LineageTimeTravelPanel: React.FC<LineageTimeTravelPanelProps> = ({
  onTimeTravelComplete,
  entityId,
  className = '',
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [selectedSnapshot, setSelectedSnapshot] = useState<string | null>(null);
  const [customDate, setCustomDate] = useState<string>('');
  const [customTime, setCustomTime] = useState<string>('');
  const [snapshotTypeFilter, setSnapshotTypeFilter] = useState<SnapshotType | 'all'>('all');

  // Fetch snapshots and stats
  const { data: snapshotsData, isLoading: snapshotsLoading } = useSnapshots(
    snapshotTypeFilter !== 'all' ? { snapshot_type: [snapshotTypeFilter] } : undefined
  );
  const { data: stats, isLoading: statsLoading } = useSnapshotStats();

  // Time travel mutation
  const timeTravelMutation = useTimeTravelMutation();

  // Filter and sort snapshots
  const snapshots = useMemo(() => {
    if (!snapshotsData?.snapshots) return [];
    return [...snapshotsData.snapshots].sort(
      (a, b) => new Date(b.snapshot_timestamp).getTime() - new Date(a.snapshot_timestamp).getTime()
    );
  }, [snapshotsData]);

  // Handle time travel to a specific snapshot
  const handleTimeTravelToSnapshot = async (snapshot: LineageSnapshot) => {
    setSelectedSnapshot(snapshot.snapshot_id);
    try {
      const result = await timeTravelMutation.mutateAsync({
        timestamp: snapshot.snapshot_timestamp,
        entity_id: entityId,
      });
      onTimeTravelComplete?.(result.graph, result.actual_timestamp);
    } catch (error) {
      console.error('Time travel failed:', error);
    }
  };

  // Handle time travel to custom date/time
  const handleTimeTravelToCustomDate = async () => {
    if (!customDate) return;
    const timestamp = customTime
      ? `${customDate}T${customTime}:00Z`
      : `${customDate}T23:59:59Z`;

    try {
      const result = await timeTravelMutation.mutateAsync({
        timestamp,
        entity_id: entityId,
      });
      onTimeTravelComplete?.(result.graph, result.actual_timestamp);
    } catch (error) {
      console.error('Time travel failed:', error);
    }
  };

  // Format timestamp for display
  const formatTimestamp = (timestamp: string): string => {
    const date = new Date(timestamp);
    return date.toLocaleString(undefined, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // Format storage size
  const formatBytes = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  // Get badge variant for snapshot type
  const getSnapshotTypeBadge = (type: SnapshotType) => {
    switch (type) {
      case 'full':
        return <Badge variant="default">Full</Badge>;
      case 'delta':
        return <Badge variant="secondary">Delta</Badge>;
      case 'triggered':
        return <Badge variant="outline">Triggered</Badge>;
      default:
        return <Badge>{type}</Badge>;
    }
  };

  return (
    <Collapsible open={isExpanded} onOpenChange={setIsExpanded}>
      <Card className={className}>
        <CollapsibleTrigger asChild>
          <CardHeader className="cursor-pointer hover:bg-muted/50 transition-colors">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <History className="h-5 w-5" />
                <CardTitle className="text-lg">Time Travel</CardTitle>
              </div>
              <div className="flex items-center gap-2">
                {stats && !statsLoading && (
                  <Badge variant="outline" className="hidden sm:flex">
                    {stats.total_snapshots} snapshots
                  </Badge>
                )}
                {isExpanded ? (
                  <ChevronUp className="h-4 w-4" />
                ) : (
                  <ChevronDown className="h-4 w-4" />
                )}
              </div>
            </div>
            <CardDescription>
              View lineage as it existed at any point in time
            </CardDescription>
          </CardHeader>
        </CollapsibleTrigger>

        <CollapsibleContent>
          <CardContent className="space-y-4">
            {/* Custom Date/Time Selection */}
            <div className="space-y-2">
              <Label className="text-sm font-medium">Jump to Date</Label>
              <div className="flex gap-2">
                <div className="flex-1">
                  <Input
                    type="date"
                    value={customDate}
                    onChange={(e) => setCustomDate(e.target.value)}
                    className="w-full"
                  />
                </div>
                <div className="w-24">
                  <Input
                    type="time"
                    value={customTime}
                    onChange={(e) => setCustomTime(e.target.value)}
                    placeholder="HH:MM"
                  />
                </div>
                <Button
                  onClick={handleTimeTravelToCustomDate}
                  disabled={!customDate || timeTravelMutation.isPending}
                  size="icon"
                >
                  <Play className="h-4 w-4" />
                </Button>
              </div>
            </div>

            {/* Snapshot Type Filter */}
            <div className="space-y-2">
              <Label className="text-sm font-medium">Snapshot Type</Label>
              <Select
                value={snapshotTypeFilter}
                onValueChange={(v) => setSnapshotTypeFilter(v as SnapshotType | 'all')}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Types</SelectItem>
                  <SelectItem value="full">Full Snapshots</SelectItem>
                  <SelectItem value="delta">Delta Snapshots</SelectItem>
                  <SelectItem value="triggered">Triggered Snapshots</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Snapshot List */}
            <div className="space-y-2">
              <Label className="text-sm font-medium">
                Recent Snapshots {snapshotsData && `(${snapshotsData.total_count} total)`}
              </Label>

              {snapshotsLoading ? (
                <div className="space-y-2">
                  {[1, 2, 3].map((i) => (
                    <Skeleton key={i} className="h-16 w-full" />
                  ))}
                </div>
              ) : snapshots.length === 0 ? (
                <div className="text-center py-4 text-muted-foreground">
                  <Clock className="h-8 w-8 mx-auto mb-2 opacity-50" />
                  <p className="text-sm">No snapshots available</p>
                </div>
              ) : (
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {snapshots.slice(0, 10).map((snapshot) => (
                    <div
                      key={snapshot.snapshot_id}
                      className={`
                        p-3 rounded-lg border cursor-pointer transition-colors
                        ${selectedSnapshot === snapshot.snapshot_id
                          ? 'border-primary bg-primary/5'
                          : 'hover:bg-muted/50'
                        }
                      `}
                      onClick={() => handleTimeTravelToSnapshot(snapshot)}
                    >
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-sm font-medium">
                          {formatTimestamp(snapshot.snapshot_timestamp)}
                        </span>
                        {getSnapshotTypeBadge(snapshot.snapshot_type)}
                      </div>
                      <div className="flex items-center gap-4 text-xs text-muted-foreground">
                        <span>{snapshot.node_count} nodes</span>
                        <span>{snapshot.edge_count} edges</span>
                        <span>{formatBytes(snapshot.storage_size_bytes)}</span>
                      </div>
                      {snapshot.trigger_reason && (
                        <p className="text-xs text-muted-foreground mt-1 truncate">
                          {snapshot.trigger_reason}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Stats Summary */}
            {stats && !statsLoading && (
              <div className="pt-2 border-t">
                <div className="grid grid-cols-3 gap-2 text-center text-xs">
                  <div>
                    <div className="font-medium">{stats.full_snapshots}</div>
                    <div className="text-muted-foreground">Full</div>
                  </div>
                  <div>
                    <div className="font-medium">{stats.delta_snapshots}</div>
                    <div className="text-muted-foreground">Delta</div>
                  </div>
                  <div>
                    <div className="font-medium">{formatBytes(stats.total_storage_bytes)}</div>
                    <div className="text-muted-foreground">Storage</div>
                  </div>
                </div>
              </div>
            )}

            {/* Loading indicator for time travel */}
            {timeTravelMutation.isPending && (
              <div className="flex items-center justify-center py-4">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary mr-2" />
                <span className="text-sm text-muted-foreground">Reconstructing lineage...</span>
              </div>
            )}

            {/* Error display */}
            {timeTravelMutation.isError && (
              <div className="text-sm text-destructive text-center py-2">
                Failed to time travel. Please try again.
              </div>
            )}
          </CardContent>
        </CollapsibleContent>
      </Card>
    </Collapsible>
  );
};

export default LineageTimeTravelPanel;
